import {
  ColDef,
  ColGroupDef,
  GetRowIdParams,
  GridApi,
  GridOptions,
  FilterOpenedEvent,
} from 'ag-grid-community';
import { Injectable, inject } from '@angular/core';
import { FilterChangeService } from '@compumark/brand-context-components';
import { Region } from '@compumark/order-management-client';

import { OrderSelectionService } from '../../selection/order-selection.service';
import {
  assigneeFilterComparator,
  assigneeSortingComparator,
  classComparator,
  customComparator,
  dateComparator,
  dateFilterComparator,
  FilterOptions,
  genericComparator,
  regionComparator,
  regionSetFilterComparator,
} from '../../sorting/sort-utils';
import {
  assigneeKeyCreatorAndValueFormatter,
  formatClasses,
  formatClassesFilterValue,
  regionKeyCreator,
  regionSetFilterValueFormatter,
  regionsFilterValueGetter,
} from './set-filter-helpers';
import { AssigneeRendererComponent } from '../assignee-renderer/assignee-renderer.component';
import { CheckboxRendererComponent } from '../checkbox-renderer/checkbox-renderer.component';
import { ClientReferenceMultiFilterComponent } from '../filtering/custom-filter-components/client-reference-multi-filter/client-reference-multi-filter.component';
import { SubClientReferenceMultiFilterComponent } from '../filtering/custom-filter-components/sub-client-reference-multi-filter/sub-client-reference-multi-filter.component';
import { enableFilterOptionTabFocus } from '../filtering/filter-option-tab-focus';
import { FreetextRendererComponent } from '../freetext-renderer/freetext-renderer.component';
import { GoodsAndServicesRendererComponent } from '../goods-and-services-renderer/goods-and-services-renderer.component';
import { LogoRendererComponent } from '../logo-renderer/logo-renderer.component';
import { RegionRendererComponent } from '../register-renderer/region-renderer.component';
import { SelectAllHeaderComponent } from '../select-all-header/select-all-header.component';
import { TrademarkRendererComponent } from '../trademark-renderer/trademark-renderer.component';
export const dateFilterParams = {
  comparator: dateFilterComparator,
  closeOnApply: true,
  maxNumConditions: 1,
  browserDatePicker: true,
  buttons: ['apply', 'clear'],
  filterOptions: [
    'equals',
    {
      displayKey: 'inRange',
      displayName: 'In range',
      predicate: (
        [startDateInRange, endDateInRange]: [Date, Date],
        cellValue: any,
      ) => {
        const cellDate = new Date(cellValue);
        return cellDate >= startDateInRange && cellDate <= endDateInRange;
      },
      numberOfInputs: 2,
    },
    {
      displayKey: 'lessThan',
      displayName: 'Less than',
      predicate: ([filterDate]: [Date], cellValue: any) => {
        const cellDate = new Date(cellValue);
        return cellDate != null && cellDate < filterDate;
      },
    },
    {
      displayKey: 'greaterThan',
      displayName: 'Greater than',
      predicate: ([filterDate]: [Date], cellValue: any) => {
        const cellDate = new Date(cellValue);
        return cellDate != null && cellDate > filterDate;
      },
    },
  ],
  maxValidYear: 9999,
};
export const textFilterParams = {
  filterOptions: ['contains', 'equals', 'startsWith', 'notEqual', 'endsWith'],
  buttons: ['apply', 'clear'],
  closeOnApply: true,
  defaultOption: 'contains',
  suppressAndOrCondition: false,
  defaultJoinOperator: 'AND',
  textMatcher: (option: any) => {
    const filterOption = option.filterOption;
    const value = option.value.replaceAll('\r\n', '').replaceAll(' ', '');
    const filterText = option.filterText.replaceAll(' ', '');
    if (filterText == null) {
      return false;
    }
    switch (filterOption) {
      case 'contains':
        return value.indexOf(filterText) >= 0;
      case 'notContains':
        return value.indexOf(filterText) < 0;
      case 'equals':
        return value === filterText;
      case 'notEqual':
        return value !== filterText;
      case 'startsWith':
        return value.indexOf(filterText) === 0;
      case 'endsWith': {
        const index = value.lastIndexOf(filterText);
        return index >= 0 && index === value.length - filterText.length;
      }
      default:
        // should never happen
        console.warn('invalid filter type ');
        return false;
    }
  },
};

@Injectable({
  providedIn: 'root',
})
export class GridOptionsService {
  private filterChangeService = inject(FilterChangeService);
  private orderSelectionService = inject(OrderSelectionService);

  private columnDefs: (ColDef | ColGroupDef)[];
  private inActColumnDefs: (ColDef | ColGroupDef)[];
  constructor() {
    this.columnDefs = [
      {
        headerComponent: SelectAllHeaderComponent,
        cellRenderer: CheckboxRendererComponent,
        field: 'checkBox',
        pinned: 'left',
        lockPinned: true,
        lockPosition: 'left',
        resizable: false,
        width: 70,
        suppressColumnsToolPanel: true,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: $localize`:@@GridServiceTrademarkColumn:Trademark`,
        field: 'trademarkName',
        cellRenderer: TrademarkRendererComponent,
        sortable: true,
        comparator: customComparator,
        sort: 'asc',
        width: 200,
        cellStyle: {
          'padding-top': '0.4rem',
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          defaultToNothingSelected: true,
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          comparator: customComparator,
          cellHeight: 40,
        },
      },
      {
        headerName: $localize`:@@GridServiceLogoColumn:Logo`,
        field: 'logo',
        valueGetter: getLogoValue,
        cellRenderer: LogoRendererComponent,
        filter: false,
        width: 140,
        sortable: false,
        cellStyle: {
          'padding-top': '1.2rem',
        },
      },
      {
        headerName: $localize`:@@GridServiceProductNameColumn:Product`,
        field: 'productName',
        width: 200,
        filter: 'agSetColumnFilter',
        filterParams: {
          defaultToNothingSelected: true,
          closeOnApply: true,
          buttons: ['apply', 'clear'],
          comparator: genericComparator,
        },
        sortable: true,
        comparator: genericComparator,
      },
      {
        headerName: $localize`:@@GridServiceReferenceColumn:Your Reference`,
        field: 'clientReference',
        width: 150,
        sortable: true,
        comparator: genericComparator,
        filter: ClientReferenceMultiFilterComponent,
      },
      {
        headerName: $localize`:@@GridServiceYourClientColumn:Your Client`,
        field: 'yourClient',
        width: 150,
        cellClass: ['capitalize', 'textWrap'],
        sortable: true,
        comparator: genericComparator,
        filter: 'agSetColumnFilter',
        filterParams: {
          refreshValuesOnOpen: true,
          defaultToNothingSelected: true,
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          comparator: genericComparator,
          values: getUniqueClientValues,
        },
      },
      {
        headerName: $localize`:@@GridServiceRegionColumn:Region`,
        cellRenderer: RegionRendererComponent,
        field: 'regions',
        width: 200,
        sortable: true,
        comparator: regionComparator,
        minWidth: 140,
        autoHeight: true,
        cellClass: ['center', 'textWrap'],
        keyCreator: regionKeyCreator,
        filterValueGetter: regionsFilterValueGetter,
        filter: 'agSetColumnFilter',
        filterParams: {
          defaultToNothingSelected: true,
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          comparator: regionSetFilterComparator,
          valueFormatter: regionSetFilterValueFormatter,
          cellHeight: 35,
        },
      },
      {
        headerName: $localize`:@@GridServiceClassesColumn:Classes`,
        field: 'classes',
        width: 200,
        sortable: true,
        comparator: classComparator,
        filter: 'agSetColumnFilter',
        valueFormatter: formatClasses,
        filterParams: {
          defaultToNothingSelected: true,
          closeOnApply: true,
          refreshValuesOnOpen: true,
          buttons: ['apply', 'clear'],
          filterOptions: FilterOptions.OPTIONS,
          values: getClassesValues,
          keyCreator: formatClassesFilterValue,
          valueFormatter: formatClassesFilterValue,
        },
      },
      {
        headerName: $localize`:@@GridServiceGoodsAndServicesColumn:Goods and Services`,
        field: 'goodsAndServices',
        cellRenderer: GoodsAndServicesRendererComponent,
        minWidth: 100,
        sortable: false,
        width: 380,
        autoHeight: true,
        cellClass: ['center', 'textWrap'],
      },
      {
        headerName: $localize`:@@GridServiceAssigneeColumn:Assignee`,
        field: 'assignee',
        cellRenderer: AssigneeRendererComponent,
        width: 200,
        keyCreator: assigneeKeyCreatorAndValueFormatter,
        filter: 'agSetColumnFilter',
        filterParams: {
          defaultToNothingSelected: true,
          closeOnApply: true,
          buttons: ['apply', 'clear'],
          comparator: assigneeFilterComparator,
          valueFormatter: assigneeKeyCreatorAndValueFormatter,
        },
        sortable: true,
        comparator: assigneeSortingComparator,
      },

      {
        headerName: $localize`:@@GridServiceStartDate:Start Date`,
        field: 'startDate',
        width: 180,
        sortable: true,
        comparator: dateComparator,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
      },
      {
        headerName: $localize`:@@GridServiceRenewalDate:Renewal Date`,
        field: 'renewalDate',
        width: 180,
        sortable: true,
        comparator: dateComparator,
        filter: 'agDateColumnFilter',
        filterParams: dateFilterParams,
      },
      {
        headerName: $localize`:@@GridServiceSubclientReference:Sub Client Reference`,
        field: 'subClientReference',
        width: 200,
        sortable: true,
        comparator: genericComparator,
        filter: SubClientReferenceMultiFilterComponent,
      },
      {
        headerName: $localize`:@@GridServiceFreeText:Free Text`,
        field: 'freeText',
        cellRenderer: FreetextRendererComponent,
        width: 200,
        minWidth: 150,
        autoHeight: true,
        cellClass: ['center', 'textWrap'],
        sortable: true,
        comparator: genericComparator,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParams,
      },
      {
        headerName: $localize`:@@GridServiceOrderNumber:Order Number`,
        field: 'orderNumber',
        width: 200,
        sortable: true,
        filter: 'agTextColumnFilter',
        filterParams: textFilterParams,
      },
    ];
    this.inActColumnDefs = [...this.columnDefs];
    this.inActColumnDefs.splice(12, 0, {
      headerName: 'Cancel Date',
      field: 'cancelDate',
      width: 180,
      sortable: true,
      comparator: dateComparator,
      filter: 'agDateColumnFilter',
      filterParams: dateFilterParams,
    });
  }
  public get gridOptions(): GridOptions {
    return {
      theme: 'legacy',
      rowModelType: 'clientSide',
      columnDefs: this.columnDefs,
      columnMenu: 'legacy',
      excelStyles: [
        {
          id: 'textWrap',
          alignment: {
            wrapText: true,
            vertical: 'Top',
          },
        },
        {
          id: 'header',
          alignment: {
            wrapText: true,
            vertical: 'Center',
            horizontal: 'Center',
          },
          font: {
            bold: true,
          },
          interior: {
            color: '#BFBFBF',
            pattern: 'Solid',
          },
          borders: {
            borderBottom: {
              color: '#CCCCCC',
              lineStyle: 'Continuous',
              weight: 1,
            },
            borderLeft: {
              color: '#FFFFFF',
              lineStyle: 'Continuous',
              weight: 1,
            },
            borderRight: {
              color: '#FFFFFF',
              lineStyle: 'Continuous',
              weight: 1,
            },
            borderTop: {
              color: '#CCCCCC',
              lineStyle: 'Continuous',
              weight: 1,
            },
          },
        },
      ],
      defaultColDef: {
        resizable: true,
        menuTabs: ['filterMenuTab', 'generalMenuTab'],
        cellStyle: {
          'padding-top': '1.9rem',
        },
        cellClass: 'textWrap',
      },
      rowHeight: 112,
      headerHeight: 43,
      rowSelection: 'multiple',
      pagination: true,
      paginationPageSize: 50,
      suppressPaginationPanel: true,
      suppressColumnVirtualisation: true,
      suppressRowHoverHighlight: true,
      suppressCellFocus: true,
      suppressRowClickSelection: true,
      suppressDragLeaveHidesColumns: true,
      getRowId: (params: GetRowIdParams) => params.data.id,
      getContextMenuItems: (params) => {
        return (
          params.defaultItems?.filter((item) => {
            return item !== 'export';
          }) || []
        );
      },
      onFilterOpened: (event: FilterOpenedEvent) => {
        enableFilterOptionTabFocus(event, [
          'clientReference',
          'subClientReference',
        ]);
      },
      onFilterChanged: (event: any) => {
        this.filterChangeService.onFilterChange(event);
        if (!this.orderSelectionService.isDataLoaded()) {
          return;
        }
        const hasActiveFilter =
          Object.keys(event.api.getFilterModel()).length > 0;
        const visibleNodes = new Set<any>();
        event.api.forEachNodeAfterFilter((node: any) => visibleNodes.add(node));
        if (hasActiveFilter) {
          event.api.forEachNode((node: any) => {
            if (!visibleNodes.has(node) && node.data?.selected) {
              node.setSelected(false);
              node.data.selected = false;
            }
          });
          const visibleSelectedIds: string[] = [];
          visibleNodes.forEach((node) => {
            if (node.data?.selected) {
              visibleSelectedIds.push(node.data.id);
            }
          });
          this.orderSelectionService
            .persistSelection(visibleSelectedIds)
            .subscribe();
        } else {
          event.api.forEachNode((node: any) => {
            const shouldBeSelected = node.data?.selected ?? false;
            if (shouldBeSelected !== node.isSelected()) {
              node.setSelected(shouldBeSelected);
            }
          });
        }
        let selectedCount = 0;
        let total = 0;
        visibleNodes.forEach((node) => {
          total++;
          if (node.data?.selected) {
            selectedCount++;
          }
        });

        if (total > 0 || hasActiveFilter) {
          this.orderSelectionService.updateFilteredSelectedCount(
            selectedCount,
            total,
            hasActiveFilter,
          );
        }
      },
      defaultExcelExportParams: {
        rowHeight: 100,
        headerRowHeight: 50,
        processCellCallback: (params: any) => {
          return getCellDataForExport(params.column.getColDef().field, params);
        },
      },
    };
  }

  private gridApi?: GridApi;

  setGridApi(api: GridApi): void {
    this.gridApi = api;
  }

  public getGridApi(): GridApi | undefined {
    return this.gridApi;
  }

  public refreshData(value: any, field: any, rowId: string): void {
    const node: any = this.gridApi?.getRowNode(rowId);
    if (node) {
      node.data[field] = value;
    }
  }

  public updateColumnDefs(prams: string): void {
    this.gridApi?.hidePopupMenu();
    this.gridApi?.setFilterModel(null);
    this.gridApi?.getColumns()?.forEach((column) => {
      this.gridApi?.destroyFilter(column);
    });
    this.gridApi?.onFilterChanged();
    if (prams === 'inactive') {
      this.gridApi?.setGridOption('columnDefs', this.inActColumnDefs);
    } else {
      this.gridApi?.setGridOption('columnDefs', this.columnDefs);
    }
  }
}
export function getCellDataForExport(column: string, params: any): string {
  switch (column) {
    case 'regions':
      return params.node.data.regions
        .filter((r: Region) => r !== null && r !== undefined)
        .sort((a: any, b: any) => {
          const labelA = a.label ?? '';
          const labelB = b.label ?? '';
          return labelA.localeCompare(labelB);
        })
        .map((r: Region) => r.label + (r.limited ? ' (Limited)' : ''))
        .filter((label: string) => label.trim() !== '')
        .join(', ');
    case 'assignee':
      return params.node.data.assignee
        ? params.node.data.assignee
        : 'Unassigned';
    case 'goodsAndServices':
      return params.value?.replace(/\r\n/g, ' ').replace(/\s+/g, ' ').trim();
    case 'classes':
      return formatClasses({ value: params.value });
    default:
      return params.value;
  }
}
export function getUniqueClientValues(params: {
  success: (values: string[]) => void;
  api: any;
}): void {
  const uniqueValues = new Set<string>();
  params.api.forEachNodeAfterFilter((node: { data?: Record<string, any> }) => {
    const value = node.data?.yourClient;
    if (value) {
      uniqueValues.add(value);
    }
  });
  params.success(Array.from(uniqueValues));
}
export function getClassesValues(params: any): any {
  const uniqueValues = new Set<string>();
  params.api.forEachNodeAfterFilter((node: { data?: Record<string, any> }) => {
    const values = node.data?.classes; // Assuming `classes` is an array
    if (Array.isArray(values)) {
      values.forEach((item: any) => uniqueValues.add(item)); // Add each item individually
    }
  });

  params.success(Array.from(uniqueValues));
}
export function getLogoValue(params: any): string {
  return params?.data?.orderNumber ?? '';
}
